<template>
  <ul class="navbar-nav me-lg-auto me-4">
    <li
      v-for="item in sortedNavItems"
      :key="item.id"
      class="nav-item dropdown hover-dropdown"
    >
      <div class="mobile-navigation-item">
        <a
          class="nav-link text-white mobile-dropdown-toggle"
          href="#"
          role="button"
          @click.prevent="emit('toggle-dropdown', $event)"
        >
          {{ item.attributes.name }}
          <i class="mobile-arrow ms-2"></i>
        </a>
      </div>

      <ul
        v-if="isLargeScreen"
        class="dropdown-menu custom-mega-menu"
        :class="{
          'multi-column': item.attributes.content.length > 1,
          showblocks: item.attributes.content.length > 1,
        }"
      >
        <!-- Header row (icons + titles) -->
        <div class="mega-menu-header">
          <template
            v-for="(content, index) in item.attributes.content"
            :key="`column_header_${content.id}`"
          >
            <div
              v-if="content.__component === 'header.nav-column'"
              :class="`column-header ${index !== item.attributes.content.length - 1 ? 'tw-mr-[20px]' : ''}`"
            >
              <img
                v-if="content.icon"
                :src="$getStrapiImageSrc(content.icon)"
                alt="icon"
                class="column-icon"
              />
              <span
                class="column-title"
                v-html="$mdRenderer.renderInline(content.name)"
              ></span>
            </div>
          </template>
        </div>
        <hr
          v-if="item.attributes.content.length > 1"
          class="mega-divider"
        />

        <!-- Link groups row -->
        <div class="mega-menu-inner">
          <template
            v-for="(content, index) in item.attributes.content"
            :key="`content_${content.id}`"
          >
            <div
              v-if="content.__component === 'header.nav-column'"
              :class="`mega-column tw-grid ${index !== item.attributes.content.length - 1 ? 'tw-mr-[20px]' : ''}`"
            >
              <ul
                class="column-links"
                :class="`${item.attributes?.name?.toLowerCase()}-dropdown`"
              >
                <li
                  v-for="columnLink in content.links"
                  :key="`column_link_${columnLink.id}`"
                  class="tw-h-fit"
                >
                  <NuxtLinkLocale
                    class="dropdown-item"
                    :to="columnLink.url"
                    :target="columnLink.openInNewTab ? '_blank' : '_self'"
                    @click.prevent="$emit('item-clicked')"
                  >
                    {{ columnLink.name }}
                  </NuxtLinkLocale>
                </li>
              </ul>
            </div>
            <div v-else>
              <ul class="column-links">
                <li
                  v-for="columnLink in content.links"
                  :key="`column_link_${columnLink.id}`"
                >
                  <NuxtLinkLocale
                    class="dropdown-item"
                    :to="columnLink.url"
                    :target="columnLink.openInNewTab ? '_blank' : '_self'"
                    @click.prevent="$emit('item-clicked')"
                  >
                    {{ columnLink.name }}
                  </NuxtLinkLocale>
                </li>
              </ul>
            </div>
          </template>
        </div>
      </ul>
      <ul
        v-else
        class="dropdown-menu"
        :class="{
          'multi-column': item.attributes.content.length > 1,
          showblocks: item.attributes.content.length > 1,
        }"
      >
        <template
          v-for="content in item.attributes.content"
          :key="`content_${content.id}`"
        >
          <!-- Simple nav group links -->
          <template v-if="content.__component === 'header.nav-group'">
            <li
              v-for="link in content.links"
              :key="`link_${link.id}`"
            >
              <NuxtLinkLocale
                class="dropdown-item"
                :to="link.url"
                :target="link.openInNewTab ? '_blank' : '_self'"
                @click.prevent="$emit('item-clicked')"
              >
                {{ link.name }}
              </NuxtLinkLocale>
            </li>
          </template>

          <!-- Column-based navigation with icons -->
          <div
            v-if="content.__component === 'header.nav-column'"
            class="dropdown-column"
            :style="{
              '--_icon': content.icon
                ? `url(${$getStrapiImageSrc(content.icon)})`
                : 'none',
            }"
          >
            <li class="dropdown-header">
              <div class="tw-flex">
                <img
                  v-if="content.icon"
                  :src="$getStrapiImageSrc(content.icon)"
                  alt="icon"
                  class="column-icon"
                />
                <span
                  class="column-title"
                  v-html="$mdRenderer.renderInline(content.name)"
                ></span>
              </div>
            </li>
            <li
              v-for="columnLink in content.links"
              :key="`column_link_${columnLink.id}`"
            >
              <NuxtLinkLocale
                class="dropdown-item"
                :to="columnLink.url"
                :target="columnLink.openInNewTab ? '_blank' : '_self'"
                @click.prevent="$emit('item-clicked')"
              >
                {{ columnLink.name }}
              </NuxtLinkLocale>
            </li>
          </div>
        </template>
      </ul>
    </li>
  </ul>
</template>

<script lang="ts" setup>
import type { NavItemsData } from '~/types/common';

const emit = defineEmits(['toggle-dropdown', 'item-clicked']);
const { data: navItems } = useCustomFetch<NavItemsData>(
  '/api/strapi/header-nav-items'
);
// const { data } = useCustomFetch<HeaderData>('/api/strapi/header');

const sortedNavItems = computed(() => {
  return navItems.value?.sort(
    (a, b) => a.attributes.menu_position - b.attributes.menu_position
  );
});

const isLargeScreen = ref();

function handleResize() {
  isLargeScreen.value = window.innerWidth >= 992;
}

onMounted(() => {
  window.addEventListener('resize', handleResize);
  handleResize();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<style lang="scss" scoped>
.custom-mega-menu {
  background: white !important;
  color: black !important;
  border-radius: 20px !important;
  padding: 15px !important;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.mega-menu-header {
  display: flex;
}

.column-header {
  display: flex;
  align-items: center;
  font-weight: bold;
  font-size: 18px;
  color: #000;
  flex: 1;
  padding-bottom: 10px;
}

.column-icon {
  width: 18px;
  height: 18px;
  margin-right: 10px;
}

.mega-divider {
  border: none;
  border-top: 2px solid #d1d1d1;
  margin: 0 0 10px 0;
}

.mega-menu-inner {
  display: flex;
}

.mega-column {
  flex: 1;
  min-width: 180px;
}

.column-links {
  list-style: none;
  padding: 0;
  margin: 0;
}
.markets-dropdown {
  display: grid;
  grid-template-columns: 100px 30px;
}
.column-title {
  font-size: 14px;
}

.column-links .dropdown-item {
  padding: 4px 0;
  color: #000;
  font-size: 13px;
  text-decoration: none;
  &:hover {
    color: var(--bs-primary);
  }
}

.mobile-navigation-item {
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  margin: 0 auto 10px auto;
  padding-top: 10px;
  width: fit-content;
  background: transparent;
}

.nav-link {
  font-weight: 500;
  padding: 0.75rem 1rem !important;
  transition: all 0.3s ease;
}

.nav-link:hover {
  color: #ffffff !important;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 6px;
}
</style>
